import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { formatNorwegianDate } from '../utils/dateUtils'
import { useMaxDataDate } from '../hooks/useMaxDataDate'

// ── Types ──────────────────────────────────────────────────────────────

export type OutputMode = 'year_to_date' | 'month_to_date' | 'last_month' | 'last_year' | 'custom'

// Getters, so every read (during render) returns the label in the active
// language. Pages index this map directly, e.g. OUTPUT_MODE_LABELS[outputMode].
export const OUTPUT_MODE_LABELS: Record<OutputMode, string> = {
  get year_to_date() { return i18n.t('components:dateFilterBar.modes.yearToDate') },
  get month_to_date() { return i18n.t('components:dateFilterBar.modes.monthToDate') },
  get last_month() { return i18n.t('components:dateFilterBar.modes.lastMonth') },
  get last_year() { return i18n.t('components:dateFilterBar.modes.lastYear') },
  get custom() { return i18n.t('components:dateFilterBar.modes.custom') },
}

const OUTPUT_MODES: OutputMode[] = ['year_to_date', 'month_to_date', 'last_month', 'last_year', 'custom']

// ── Date computation helpers ───────────────────────────────────────────

export { formatNorwegianDate }

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Compute a concrete {startDate, endDate} (YYYY-MM-DD strings) from a given
 * output mode.  When a `maxDate` is supplied the end date for
 * year_to_date / month_to_date is capped to that date.
 */
export function getDateRangeForMode(
  mode: OutputMode,
  maxDate?: Date | null,
): { startDate: string; endDate: string } {
  const now = new Date()
  let start: Date
  let end: Date

  switch (mode) {
    case 'year_to_date':
      start = new Date(now.getFullYear(), 0, 1)
      end = maxDate ? new Date(maxDate) : new Date(now)
      break
    case 'month_to_date': {
      start = new Date(now.getFullYear(), now.getMonth(), 1)
      end = maxDate ? new Date(maxDate) : new Date(now)
      // If data cutoff is before the current month, fall back to the cutoff's month
      if (maxDate && end < start) {
        start = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
        end = new Date(maxDate)
      }
      break
    }
    case 'last_month': {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      end = new Date(now.getFullYear(), now.getMonth(), 0)
      break
    }
    case 'last_year':
      start = new Date(now.getFullYear() - 1, 0, 1)
      end = new Date(now.getFullYear() - 1, 11, 31)
      break
    default: // 'custom' – return year-to-date as fallback
      start = new Date(now.getFullYear(), 0, 1)
      end = maxDate ? new Date(maxDate) : new Date(now)
      break
  }

  // Ensure end is never before start (can happen when maxDate is before the period start,
  // e.g. "Hittil i måneden" in March but loan data only goes to mid-February)
  if (end < start) {
    end = new Date(start)
  }

  return {
    startDate: toLocalDateStr(start),
    endDate: toLocalDateStr(end),
  }
}

// ── Props ──────────────────────────────────────────────────────────────

interface DateFilterBarProps {
  /**
   * **outputMode mode** — caller tracks the mode string and receives it
   * back together with optional custom dates.
   */
  outputMode: OutputMode
  onOutputModeChange: (mode: OutputMode) => void

  /** Custom start date (YYYY-MM-DD) – only used when mode === 'custom' */
  customStartDate?: string
  onCustomStartDateChange?: (v: string) => void

  /** Custom end date (YYYY-MM-DD) – only used when mode === 'custom' */
  customEndDate?: string
  onCustomEndDateChange?: (v: string) => void

  /** Called when the user presses "Oppdater" */
  onApply: () => void

  /**
   * Hide this bar's own "Oppdater" button.
   *
   * For a screen where the period is only one of several filters and they all
   * commit together from a single button further down (Bruktbil, #526) — two
   * Oppdater buttons on one screen is exactly the ambiguity that change set out
   * to remove. Defaults to false, so every existing caller is unaffected.
   */
  hideApply?: boolean

  /** Modes to completely hide from the bar */
  hiddenModes?: OutputMode[]

  /** Extra content rendered *inside* the bar (e.g. min-registreringer input) */
  children?: React.ReactNode
  /** Content rendered below the main row, separated by a border-top divider (e.g. PostnummerFilter compact) */
  bottomContent?: React.ReactNode

  /** Maximum selectable date (YYYY-MM-DD) — sets max attribute on date inputs */
  maxDataDate?: string | null
}

// ── Component ──────────────────────────────────────────────────────────

export default function DateFilterBar({
  outputMode,
  onOutputModeChange,
  customStartDate = '',
  onCustomStartDateChange,
  customEndDate = '',
  onCustomEndDateChange,
  onApply,
  hideApply = false,
  hiddenModes,
  children,
  bottomContent,
  maxDataDate: maxDataDateProp,
}: DateFilterBarProps) {
  const { t } = useTranslation('components')
  const fetchedMaxDate = useMaxDataDate()
  const effectiveMaxDate = maxDataDateProp ?? fetchedMaxDate

  const isCustom = outputMode === 'custom'
  const customReady = !isCustom || (!!customStartDate && !!customEndDate)

  // Compute resolved date range for preset modes so users see the actual dates
  const resolvedRange = !isCustom && effectiveMaxDate
    ? getDateRangeForMode(outputMode, new Date(effectiveMaxDate + 'T12:00:00'))
    : null

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('dateFilterBar.period')}</span>

        {/* Period preset buttons */}
        <div className="flex flex-wrap gap-2">
          {OUTPUT_MODES.filter(m => !hiddenModes?.includes(m)).map(mode => (
            <button
              key={mode}
              onClick={() => onOutputModeChange(mode)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                outputMode === mode
                  ? 'bg-primary-600 text-white shadow-xs'
                  : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
              }`}
            >
              {OUTPUT_MODE_LABELS[mode]}
            </button>
          ))}
        </div>

        {/* Custom date inputs (visible only in "Annen periode" mode) */}
        {isCustom && (
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dateFilterBar.startDate')}
              </label>
              <input
                type="date"
                lang="nb"
                value={customStartDate}
                onChange={e => {
                  const val = e.target.value
                  if (effectiveMaxDate && val > effectiveMaxDate) {
                    onCustomStartDateChange?.(effectiveMaxDate)
                  } else {
                    onCustomStartDateChange?.(val)
                  }
                }}
                max={effectiveMaxDate || undefined}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dateFilterBar.endDate')}
              </label>
              <input
                type="date"
                lang="nb"
                value={customEndDate}
                onChange={e => {
                  const val = e.target.value
                  if (effectiveMaxDate && val > effectiveMaxDate) {
                    onCustomEndDateChange?.(effectiveMaxDate)
                  } else {
                    onCustomEndDateChange?.(val)
                  }
                }}
                max={effectiveMaxDate || undefined}
                className="input-field"
              />
            </div>
          </div>
        )}

        {/* Resolved date range for preset modes */}
        {resolvedRange && (
          <span className="text-xs text-gray-500 dark:text-gray-400 italic">
            {formatNorwegianDate(resolvedRange.startDate)} &rarr; {formatNorwegianDate(resolvedRange.endDate)}
          </span>
        )}

        {/* Optional extra content (e.g. filters specific to a page) */}
        {children}

        {/* Apply button */}
        {!hideApply && (
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => { if (customReady) onApply() }}
              className={`btn-primary text-sm ${!customReady ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!customReady}
            >
              {t('dateFilterBar.apply')}
            </button>
          </div>
        )}
      </div>
      {bottomContent && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
          {bottomContent}
        </div>
      )}
    </div>
  )
}
